import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Loader2, Lock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_readiness-check-27/artifacts/el8npnpt_CXOlogo.png";

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    work_email: '',
    org_name: '',
    phone: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchConfig();
    loadRazorpayScript();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/config`);
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch config:', error);
    }
  };

  const loadRazorpayScript = () => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
    if (!formData.work_email.trim()) newErrors.work_email = 'Work email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.work_email)) {
      newErrors.work_email = 'Please enter a valid email';
    }
    if (!formData.org_name.trim()) newErrors.org_name = 'Organisation name is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePayment = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      // Create order
      const orderResponse = await axios.post(`${API}/payment/create-order`, formData);
      const { order_id, amount, key_id, mock_mode } = orderResponse.data;

      if (mock_mode || !key_id) {
        // Mock payment for development
        toast.info('Payment gateway not configured. Processing mock payment...');
        
        const verifyResponse = await axios.post(`${API}/payment/verify`, {
          razorpay_order_id: order_id,
          razorpay_payment_id: `pay_mock_${Date.now()}`,
          razorpay_signature: 'mock_signature',
          customer_data: formData
        });

        if (verifyResponse.data.success) {
          navigate('/payment-success', {
            state: {
              username: verifyResponse.data.username,
              display_username: verifyResponse.data.display_username,
              work_email: verifyResponse.data.work_email
            }
          });
        }
        return;
      }

      // Razorpay checkout
      const options = {
        key: key_id,
        amount: amount,
        currency: 'INR',
        name: 'CodeXero',
        description: 'DPDP Readiness Assessment',
        order_id: order_id,
        handler: async (response) => {
          try {
            const verifyResponse = await axios.post(`${API}/payment/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              customer_data: formData
            });

            if (verifyResponse.data.success) {
              navigate('/payment-success', {
                state: {
                  username: verifyResponse.data.username,
                  display_username: verifyResponse.data.display_username,
                  work_email: verifyResponse.data.work_email
                }
              });
            }
          } catch (error) {
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        prefill: {
          name: `${formData.first_name} ${formData.last_name}`,
          email: formData.work_email,
          contact: formData.phone || ''
        },
        theme: {
          color: '#003366'
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
            toast.info('Payment cancelled');
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" data-testid="header-logo">
            <img src={LOGO_URL} alt="CodeXero" className="h-10" />
          </Link>
        </div>
      </header>

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <Link to="/pricing" className="flex items-center gap-2 text-slate-500 hover:text-[#008080] transition-colors" data-testid="back-link">
          <ArrowLeft className="h-4 w-4" />
          Back to Pricing
        </Link>
      </div>

      {/* Checkout Form */}
      <section className="py-8 pb-20">
        <div className="max-w-xl mx-auto px-6">
          <div className="bg-white border border-slate-200 rounded-sm p-8 shadow-[0_4px_24px_rgba(0,51,102,0.06)]">
            <h1 className="text-2xl font-[800] text-[#003366] mb-2" data-testid="checkout-heading">
              Checkout
            </h1>
            <p className="text-slate-600 mb-8">Complete your details to proceed with payment</p>

            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name" className="text-[#003366] font-medium">First Name *</Label>
                  <Input
                    id="first_name"
                    name="first_name"
                    data-testid="checkout-first-name"
                    value={formData.first_name}
                    onChange={handleChange}
                    className={`mt-1 rounded-sm ${errors.first_name ? 'border-red-500' : ''}`}
                    placeholder="Priya"
                  />
                  {errors.first_name && <p className="text-red-500 text-sm mt-1">{errors.first_name}</p>}
                </div>
                <div>
                  <Label htmlFor="last_name" className="text-[#003366] font-medium">Last Name *</Label>
                  <Input
                    id="last_name"
                    name="last_name"
                    data-testid="checkout-last-name"
                    value={formData.last_name}
                    onChange={handleChange}
                    className={`mt-1 rounded-sm ${errors.last_name ? 'border-red-500' : ''}`}
                    placeholder="Sharma"
                  />
                  {errors.last_name && <p className="text-red-500 text-sm mt-1">{errors.last_name}</p>}
                </div>
              </div>

              <div>
                <Label htmlFor="work_email" className="text-[#003366] font-medium">Work Email *</Label>
                <Input
                  id="work_email"
                  name="work_email"
                  type="email"
                  data-testid="checkout-email"
                  value={formData.work_email}
                  onChange={handleChange}
                  className={`mt-1 rounded-sm ${errors.work_email ? 'border-red-500' : ''}`}
                  placeholder="priya@company.com"
                />
                {errors.work_email && <p className="text-red-500 text-sm mt-1">{errors.work_email}</p>}
                <p className="text-slate-500 text-sm mt-1">Login credentials will be sent to this email</p>
              </div>

              <div>
                <Label htmlFor="org_name" className="text-[#003366] font-medium">Organisation Name *</Label>
                <Input
                  id="org_name"
                  name="org_name"
                  data-testid="checkout-org-name"
                  value={formData.org_name}
                  onChange={handleChange}
                  className={`mt-1 rounded-sm ${errors.org_name ? 'border-red-500' : ''}`}
                  placeholder="Acme Pvt Ltd"
                />
                {errors.org_name && <p className="text-red-500 text-sm mt-1">{errors.org_name}</p>}
              </div>

              <div>
                <Label htmlFor="phone" className="text-[#003366] font-medium">Phone Number (Optional)</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  data-testid="checkout-phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="mt-1 rounded-sm"
                  placeholder="+91 98765 43210"
                />
              </div>

              <div className="border-t border-slate-200 pt-6 mt-8">
                <div className="flex justify-between items-center mb-6">
                  <span className="text-slate-600">DPDP Readiness Assessment</span>
                  <span className="text-2xl font-[800] text-[#003366]">₹{config?.price_display || '—'}</span>
                </div>
                
                <Button
                  onClick={handlePayment}
                  disabled={loading}
                  data-testid="proceed-to-pay-btn"
                  className="w-full bg-[#003366] hover:bg-[#002244] text-white text-lg py-6 rounded-sm btn-hover"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Lock className="mr-2 h-5 w-5" />
                      Proceed to Pay ₹{config?.price_display || '—'}
                    </>
                  )}
                </Button>

                <p className="text-center text-slate-500 text-sm mt-4">
                  Secure payment powered by Razorpay
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#003366] text-white py-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <img src={LOGO_URL} alt="CodeXero" className="h-8 brightness-0 invert mx-auto mb-4" />
          <p className="text-slate-400 text-sm">© CodeXero {new Date().getFullYear()}</p>
        </div>
      </footer>
    </div>
  );
};

export default CheckoutPage;
